import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Plus, Edit2, Loader2, Receipt, Download, Upload } from 'lucide-react';
import { api } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { B2cPricingForm } from './pricing/B2cPricingForm';
import { B2bPricingForm } from './pricing/B2bPricingForm';
import { ImportPricingDialog } from './pricing/ImportPricingDialog';

interface PricingPlan {
  id: number;
  serviceType: 'B2C' | 'B2B';
  courierIds: number[];
  courierNames?: string[];
  fuelChargePercent?: number;
  config: any;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export function MerchantPricingTab({ merchantId }: { merchantId: number }) {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [createMode, setCreateMode] = useState<'B2C' | 'B2B'>('B2C');
  const [editing, setEditing] = useState<PricingPlan | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [exporting, setExporting] = useState<'' | 'B2C' | 'B2B'>('');

  const handleExport = async (serviceType: 'B2C' | 'B2B') => {
    setExporting(serviceType);
    try {
      const r = await api.get(`/merchants/${merchantId}/pricing/export`, { params: { serviceType }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `pricing-${merchantId}-${serviceType}-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally { setExporting(''); }
  };

  const { data: plans, isLoading } = useQuery({
    queryKey: ['merchant-pricing', merchantId],
    queryFn: async () => (await api.get<PricingPlan[]>(`/merchants/${merchantId}/pricing`)).data,
  });

  const del = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/merchants/${merchantId}/pricing/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-pricing', merchantId] }); toast.success('Pricing plan deactivated'); },
  });
  const activate = useMutation({
    mutationFn: async (id: number) => (await api.patch(`/merchants/${merchantId}/pricing/${id}`, { isActive: true })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['merchant-pricing', merchantId] }); toast.success('Pricing plan activated'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Activate failed'),
  });

  // B2C / B2B toggle. A merchant on both services carries two rate cards that share nothing —
  // different couriers, different columns, different rules — so a single mixed list forces you to
  // read the Service Type badge on every row to find the one you came for.
  const [tab, setTab] = useState<'ALL' | 'B2C' | 'B2B'>('ALL');
  const visiblePlans = (plans ?? []).filter(p => tab === 'ALL' || p.serviceType === tab);

  // Counts describe WHAT IS ON SCREEN, so they can't contradict the list under them.
  const activeCount = visiblePlans.filter(p => p.isActive).length;
  const inactiveCount = visiblePlans.filter(p => !p.isActive).length;
  const b2cTotal = (plans ?? []).filter(p => p.serviceType === 'B2C').length;
  const b2bTotal = (plans ?? []).filter(p => p.serviceType === 'B2B').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Stat label={tab === 'ALL' ? 'Active Services' : `Active ${tab}`} value={activeCount} variant="success" />
          <Stat label={tab === 'ALL' ? 'Inactive Services' : `Inactive ${tab}`} value={inactiveCount} variant="danger" />
          <Stat label={tab === 'ALL' ? 'Total Services' : `Total ${tab}`} value={visiblePlans.length} variant="primary" />
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => handleExport('B2C')} disabled={!!exporting}
            className="px-3 py-2 text-sm border border-blue-300 text-blue-700 rounded-lg font-medium hover:bg-blue-50 flex items-center gap-2 disabled:opacity-60">
            <Download size={14} /> {exporting === 'B2C' ? 'Exporting...' : 'Export B2C'}
          </button>
          <button onClick={() => handleExport('B2B')} disabled={!!exporting}
            className="px-3 py-2 text-sm border border-purple-300 text-purple-700 rounded-lg font-medium hover:bg-purple-50 flex items-center gap-2 disabled:opacity-60">
            <Download size={14} /> {exporting === 'B2B' ? 'Exporting...' : 'Export B2B'}
          </button>
          <button onClick={() => setShowImport(true)}
            className="px-3 py-2 text-sm border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 flex items-center gap-2">
            <Upload size={14} /> Import
          </button>
          <button onClick={() => { setCreateMode('B2C'); setShowCreate(true); }}
            className="px-3 py-2 text-sm border border-blue-300 text-blue-700 rounded-lg font-medium hover:bg-blue-50 flex items-center gap-2">
            <Plus size={14} /> Add B2C Pricing
          </button>
          <button onClick={() => { setCreateMode('B2B'); setShowCreate(true); }}
            className="px-3 py-2 text-sm border border-purple-300 text-purple-700 rounded-lg font-medium hover:bg-purple-50 flex items-center gap-2">
            <Plus size={14} /> Add B2B Pricing
          </button>
        </div>
      </div>

      {/* Courier service toggle */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {([
          { key: 'ALL', label: 'All', count: plans?.length ?? 0, tone: 'text-emerald-700' },
          { key: 'B2C', label: 'B2C Couriers', count: b2cTotal, tone: 'text-blue-700' },
          { key: 'B2B', label: 'B2B Couriers', count: b2bTotal, tone: 'text-purple-700' },
        ] as const).map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 text-sm rounded-md transition flex items-center gap-2 ${
              tab === t.key ? `bg-white shadow-sm font-medium ${t.tone}` : 'text-slate-600 hover:text-slate-900'
            }`}>
            {t.label}
            <span className={`text-[10px] px-1.5 rounded-full ${tab === t.key ? 'bg-slate-100 text-slate-600' : 'bg-slate-200 text-slate-500'}`}>{t.count}</span>
          </button>
        ))}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-700 text-white text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Courier Name</th>
                <th className="text-left px-4 py-3">Service Type</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-left px-4 py-3">Updated</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Loading...</td></tr>}
              {!isLoading && visiblePlans.length === 0 && (
                <tr><td colSpan={7} className="text-center py-8 text-slate-400">
                  <Receipt size={20} className="mx-auto mb-1 opacity-50" />
                  {(plans?.length ?? 0) === 0
                    ? 'No pricing plans yet. Add B2C or B2B pricing above.'
                    : `No ${tab} pricing plans — this merchant has ${plans?.length} plan(s) on the other service.`}
                </td></tr>
              )}
              {visiblePlans.map((p) => (
                <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-slate-500 text-xs">{p.id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">
                      {(p.courierNames ?? []).map((n, i) => (
                        <span key={i} className="inline-flex items-center gap-1 mr-2">
                          <span className="text-emerald-600">🚚</span>{n}
                          <span className="text-xs text-slate-400 font-mono">#{p.courierIds[i]}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.serviceType === 'B2C' ? 'bg-blue-50 text-blue-700' : 'bg-purple-50 text-purple-700'}`}>
                      {p.serviceType}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{new Date(p.createdAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{new Date(p.updatedAt).toLocaleDateString('en-IN')}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${p.isActive ? 'text-success' : 'text-slate-400'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${p.isActive ? 'bg-success' : 'bg-slate-300'}`} />
                      {p.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEditing(p)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary/10 rounded" title="Edit"><Edit2 size={14} /></button>
                      {p.isActive ? (
                        <button onClick={() => { if (confirm('Deactivate this plan?')) del.mutate(p.id); }}
                          className="text-xs text-danger hover:underline px-2" title="Deactivate">
                          {del.isPending ? <Loader2 size={12} className="animate-spin" /> : 'Deactivate'}
                        </button>
                      ) : (
                        <button onClick={() => activate.mutate(p.id)}
                          className="text-xs text-emerald-700 font-medium hover:underline px-2" title="Activate">
                          {activate.isPending ? <Loader2 size={12} className="animate-spin" /> : 'Activate'}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)}
        title={`Add ${createMode} Pricing`} subtitle="Configure courier pricing rules" maxWidth="5xl">
        {createMode === 'B2C'
          ? <B2cPricingForm merchantId={merchantId} onSuccess={() => setShowCreate(false)} />
          : <B2bPricingForm merchantId={merchantId} onSuccess={() => setShowCreate(false)} />}
      </Modal>

      <Modal isOpen={showImport} onClose={() => setShowImport(false)} title="Import Pricing Plans (CSV)" subtitle="Bulk-add or update merchant pricing" maxWidth="2xl">
        <ImportPricingDialog merchantId={merchantId} onClose={() => setShowImport(false)} />
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)}
        title={`Edit ${editing?.serviceType ?? ''} Pricing`} subtitle={`Plan #${editing?.id}`} maxWidth="5xl">
        {editing && editing.serviceType === 'B2C' &&
          <B2cPricingForm merchantId={merchantId} planId={editing.id} defaultValues={editing} onSuccess={() => setEditing(null)} />}
        {editing && editing.serviceType === 'B2B' &&
          <B2bPricingForm merchantId={merchantId} planId={editing.id} defaultValues={editing} onSuccess={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}

function Stat({ label, value, variant }: { label: string; value: number; variant: 'success' | 'danger' | 'primary' }) {
  const map = {
    success: 'border-green-200 bg-green-50 text-green-700',
    danger: 'border-red-200 bg-red-50 text-red-700',
    primary: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };
  return (
    <div className={`px-3 py-2 rounded-lg border ${map[variant]} flex items-center gap-2 text-sm`}>
      <span className="font-bold">{value}</span>
      <span className="text-xs opacity-80">{label}</span>
    </div>
  );
}
